import React, { Suspense, useEffect } from "react";
import { defineMessages, useIntl } from "react-intl";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { useAccountLookup } from "@/api/accounts";
import Helmet from "@/components/helmet";
import { Column, Layout, Tabs } from "@/components/ui";
import { useAuth } from "@/contexts/auth/useAuth";
import { useModalContext } from "@/contexts/modal-context";
import Header from "@/features/account/components/header";
import Title from "@/features/account/components/title";
import PlaceholderProfile from "@/features/placeholder/components/placeholder-profile";
import LinkFooter from "@/features/ui/components/link-footer";
import {
  ProfileInfoPanel,
  ProfileMediaPanel,
  SignUpPanel,
  CtaBanner,
  SuggestedGroupsPanel,
} from "@/features/ui/util/async-components";
import { useOnScreen } from "@/hooks";
import { useAccountRouteParams } from "@/hooks/useAccountRouteParams";

interface ProfilePageProps {
  children: React.ReactNode;
}

const messages = defineMessages({
  posts: { id: "account.posts", defaultMessage: "Truths" },
  posts_with_replies: {
    id: "account.posts_with_replies",
    defaultMessage: "Replies",
  },
  media: { id: "account.media", defaultMessage: "Media" },
  favourites: { id: "navigation_bar.favourites", defaultMessage: "Likes" },
});

/** Page to display a user's profile. */
const ProfilePage: React.FC<ProfilePageProps> = ({ children }) => {
  const location = useLocation();
  const { username = "" } = useAccountRouteParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const { openModal } = useModalContext();

  const shouldInitiateFollow =
    new URLSearchParams(window.location.search).get("initiate-follow") ===
    "true";

  const [isActionButtonVisible, currentElement] = useOnScreen<HTMLDivElement>(
    -162,
    100,
    true,
  );

  const {
    account,
    isLoading: isAccountLoading,
    isError,
  } = useAccountLookup(username, { withRelationship: true });

  const { currentAccount } = useAuth();

  useEffect(() => {
    if (
      shouldInitiateFollow &&
      currentAccount &&
      account &&
      currentAccount.id !== account?.id
    ) {
      openModal("FOLLOW", { account });
    }
  }, [account?.id]);

  useEffect(() => {
    if (!currentAccount) {
      openModal("AD");
    }
  }, [currentAccount]);

  useEffect(() => {
    if (isAccountLoading || isError) return;
  }, [isAccountLoading, isError]);

  // Fix case of username
  if (account && account.acct !== username) {
    return <Navigate to={`/@${account.acct}`} replace />;
  }

  const tabItems: React.ComponentProps<typeof Tabs>["items"] = [
    {
      label: intl.formatMessage(messages.posts),
      action: () => navigate(`/@${username}`),
      name: "profile",
    },
    {
      label: intl.formatMessage(messages.posts_with_replies),
      action: () =>
        currentAccount
          ? navigate(`/@${username}/with_replies`)
          : openModal("UNAUTHORIZED"),
      name: "replies",
    },
    {
      label: intl.formatMessage(messages.media),
      action: () =>
        currentAccount
          ? navigate(`/@${username}/media`)
          : openModal("UNAUTHORIZED"),
      name: "media",
    },
  ];

  if (account && currentAccount) {
    const ownAccount = account.id === currentAccount.id;
    if (ownAccount) {
      tabItems.push({
        label: intl.formatMessage(messages.favourites),
        action: () => navigate(`/@${username}/favorites`),
        name: "likes",
      });
    }
  }

  let activeItem;
  const pathname = location.pathname.replace(`@${username}/`, "");
  if (pathname.endsWith("/with_replies")) {
    activeItem = "replies";
  } else if (pathname.endsWith("/media")) {
    activeItem = "media";
  } else if (pathname.endsWith("/favorites")) {
    activeItem = "likes";
  } else {
    activeItem = "profile";
  }

  return (
    <>
      <Layout.Main>
        <Column
          labelElement={
            account && (
              <Title account={account} showActions={!isActionButtonVisible} />
            )
          }
          withHeader={!!account}
        >
          {account && (
            <Helmet
              title={`${account.display_name} (@${username})`}
              description={account.source.note}
              imageUrl={account.avatar_static}
            />
          )}

          <div className="-m-4">
            {isAccountLoading ? (
              <PlaceholderProfile />
            ) : (
              <div className="space-y-4">
                <Header account={account} />

                <div ref={currentElement} />

                <Suspense fallback={null}>
                  <ProfileInfoPanel username={username} account={account} />
                </Suspense>

                {account && (
                  <Tabs
                    key={`profile-tabs-${account.id}`}
                    items={tabItems}
                    activeItem={activeItem}
                  />
                )}
              </div>
            )}

            {children}
          </div>
        </Column>

        {!currentAccount && (
          <Suspense fallback={null}>
            <CtaBanner key="cta-banner" />
          </Suspense>
        )}
      </Layout.Main>

      <Layout.Aside>
        {!currentAccount && (
          <Suspense fallback={null}>
            <SignUpPanel key="sign-up-panel" />
          </Suspense>
        )}
        <Suspense fallback={null}>
          <ProfileMediaPanel account={account} />
        </Suspense>
        {currentAccount && (
          <Suspense fallback={null}>
            <SuggestedGroupsPanel key="suggested-groups-panel" />
          </Suspense>
        )}
        <LinkFooter key="link-footer" />
      </Layout.Aside>
    </>
  );
};

export default ProfilePage;
